// Low-level Web Audio building blocks shared by SFX, loops and music.
// Everything takes a BaseAudioContext so the exact same graphs render live
// (AudioContext) or offline (OfflineAudioContext, used by the audio sandbox to
// measure every sound). Generated buffers (noise, reverb impulses, distortion
// curves) are cached and shared: an AudioBuffer may be used by any context
// with the same sample rate.

export type NoiseColor = 'white' | 'pink' | 'brown';

/** Smallest value exponential ramps may target (≈ -80 dB). */
export const SILENT = 1e-4;

const NOISE_SECONDS = 2;
const bufferCache = new Map<string, AudioBuffer>();
const curveCache = new Map<number, Float32Array<ArrayBuffer>>();

export function mtof(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

const NOTE_INDEX: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** 'C4' → 60, 'F#3' → 54, 'Bb2' → 46. */
export function note(name: string): number {
  const m = /^([A-G])([#b]?)(-?\d)$/.exec(name);
  if (!m) throw new Error(`bad note ${name}`);
  const acc = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return 12 * (Number(m[3]) + 1) + NOTE_INDEX[m[1]] + acc;
}

/** Frequency of a note name. */
export function hz(name: string): number {
  return mtof(note(name));
}

/**
 * Seamlessly looping mono noise (2 s). The tail is cross-faded into the head so
 * long loops (rain, engines) never click at the wrap point.
 */
export function noiseBuffer(ctx: BaseAudioContext, color: NoiseColor = 'white'): AudioBuffer {
  const key = `noise:${color}:${ctx.sampleRate}`;
  const cached = bufferCache.get(key);
  if (cached) return cached;
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * NOISE_SECONDS);
  const fade = Math.floor(sr * 0.05);
  const raw = new Float32Array(len + fade);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, last = 0;
  for (let i = 0; i < raw.length; i++) {
    const w = Math.random() * 2 - 1;
    if (color === 'white') {
      raw[i] = w * 0.9;
    } else if (color === 'pink') {
      // Paul Kellet's refined pink filter.
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      raw[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    } else {
      last = (last + 0.02 * w) / 1.02;
      raw[i] = last * 3.5;
    }
  }
  const buf = ctx.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = raw[i];
  for (let i = 0; i < fade; i++) {
    const k = i / fade;
    d[i] = raw[i] * k + raw[len + i] * (1 - k);
  }
  bufferCache.set(key, buf);
  return buf;
}

/**
 * Generated stereo convolution impulse: a few early reflections, then decaying
 * noise that gets darker over time (one-pole lowpass whose cutoff falls).
 */
export function reverbImpulse(ctx: BaseAudioContext, seconds: number, brightness: number, preDelay = 0.012): AudioBuffer {
  const key = `ir:${seconds}:${brightness}:${preDelay}:${ctx.sampleRate}`;
  const cached = bufferCache.get(key);
  if (cached) return cached;
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * seconds);
  const buf = ctx.createBuffer(2, len, sr);
  const pre = Math.floor(preDelay * sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    for (let i = pre; i < len; i++) {
      const t = (i - pre) / (len - pre);
      // Cutoff coefficient falls from bright to dark over the tail.
      const coef = Math.max(0.02, brightness * (1 - t * 0.85));
      lp += coef * (Math.random() * 2 - 1 - lp);
      d[i] = lp * Math.pow(1 - t, 2.2) * (0.6 + brightness * 0.4);
    }
    // Early reflections (slightly different per channel for width).
    for (let r = 0; r < 6; r++) {
      const at = pre + Math.floor(sr * (0.007 + r * 0.011 + (ch ? 0.0031 : 0) + Math.random() * 0.004));
      if (at < len) d[at] += (Math.random() < 0.5 ? -1 : 1) * 0.5 * Math.pow(0.72, r);
    }
  }
  bufferCache.set(key, buf);
  return buf;
}

/** Symmetric tanh soft-clip curve; `amount` 1 (warm) .. 30 (fuzz). */
export function driveCurve(amount: number): Float32Array<ArrayBuffer> {
  const k = Math.max(0.5, Math.round(amount * 2) / 2);
  const cached = curveCache.get(k);
  if (cached) return cached;
  const n = 1024;
  const c = new Float32Array(n);
  const norm = Math.tanh(k);
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / (n - 1) - 1;
    c[i] = Math.tanh(k * x) / norm;
  }
  curveCache.set(k, c);
  return c;
}

// ───────────────────────────── Voice ─────────────────────────────

export interface Env {
  /** Attack seconds (linear). */
  a?: number;
  /** Hold seconds at peak. */
  h?: number;
  /** Decay seconds (exponential to silence, or linear with `lin`). */
  d: number;
  peak?: number;
  lin?: boolean;
}

export interface FilterSpec {
  type?: BiquadFilterType;
  f: number;
  /** Sweep target (exponential) reached `dur` seconds after `at`. */
  f1?: number;
  /** Optional second sweep target reached `dur2` after the first. */
  f2?: number;
  at?: number;
  dur?: number;
  dur2?: number;
  q?: number;
  gain?: number;
}

/** Frequency trajectory: start `f`, exponential sweep to `f1` over `fdur`, optional `f2` over `f2dur`. */
export interface PitchSpec {
  f: number;
  f1?: number;
  fdur?: number;
  f2?: number;
  f2dur?: number;
  /** Delay before the sweep starts (relative to the note). */
  fat?: number;
  /** Vibrato [rate Hz, depth cents]. */
  vib?: [number, number];
}

export interface ToneSpec extends Env, PitchSpec {
  type?: OscillatorType;
  at?: number;
  detune?: number;
  filter?: FilterSpec;
  dist?: number;
  to?: AudioNode;
}

export interface NoiseSpec extends Env {
  color?: NoiseColor;
  at?: number;
  /** Playback rate (pitch of the noise; follows voice pitch). */
  rate?: number;
  filter?: FilterSpec;
  filter2?: FilterSpec;
  dist?: number;
  to?: AudioNode;
}

export interface FmSpec extends ToneSpec {
  /** Modulator frequency = carrier × ratio. */
  ratio: number;
  /** Modulation index at the start … */
  index: number;
  /** … decaying linearly to this over `idur` (defaults to the envelope length). */
  index1?: number;
  idur?: number;
  modType?: OscillatorType;
}

export interface CrackleSpec {
  at?: number;
  dur: number;
  /** Grains per second. */
  rate: number;
  /** Grain length seconds (randomised ±50%). */
  grain?: number;
  peak: number;
  /** Grain amplitude fades out across `dur`. */
  fade?: boolean;
  color?: NoiseColor;
  filter?: FilterSpec;
  to?: AudioNode;
}

export interface ChainSpec {
  filter?: FilterSpec;
  filter2?: FilterSpec;
  dist?: number;
  gain?: number;
  to?: AudioNode;
}

let liveNodes = 0;
/** Debug: number of nodes created by voices that have not been disconnected yet. */
export function liveNodeCount(): number {
  return liveNodes;
}

/**
 * One self-cleaning sound: every node it creates is tracked, every source is
 * scheduled with a stop time, and once the last source ends the whole graph is
 * disconnected. Frequencies are multiplied by the voice pitch `p` (filters too,
 * so a pitched sound keeps its timbre). Times passed to helpers are relative to
 * the voice start `t`.
 */
export class Voice {
  readonly ctx: BaseAudioContext;
  readonly t: number;
  readonly p: number;
  readonly out: AudioNode;
  /** Latest scheduled source stop time (absolute). */
  end = 0;
  /** Base frequency params registered for live pitch changes (loops). */
  readonly pitchParams: { param: AudioParam; base: number }[] = [];
  private nodes: AudioNode[] = [];
  private srcs: AudioScheduledSourceNode[] = [];
  private lastSrc: AudioScheduledSourceNode | null = null;
  /** A source started without a stop time (loops); it defines the end after kill(). */
  private openSrc: AudioScheduledSourceNode | null = null;
  private done = false;
  private readonly nyq: number;
  /** Called once after the graph has been disconnected. */
  onDone: (() => void) | null = null;

  constructor(ctx: BaseAudioContext, out: AudioNode, t: number, pitch = 1) {
    this.ctx = ctx;
    this.out = out;
    this.t = t;
    this.p = pitch;
    this.nyq = ctx.sampleRate * 0.45;
  }

  /** Pitch-scaled, clamped frequency. */
  hz(f: number): number {
    return Math.max(1, Math.min(this.nyq, f * this.p));
  }

  track<T extends AudioNode>(n: T): T {
    this.nodes.push(n);
    liveNodes++;
    return n;
  }

  gain(value = 1, to: AudioNode | null = this.out): GainNode {
    const g = this.track(this.ctx.createGain());
    g.gain.value = value;
    if (to) g.connect(to);
    return g;
  }

  filter(spec: FilterSpec, to: AudioNode | null = this.out): BiquadFilterNode {
    const f = this.track(this.ctx.createBiquadFilter());
    f.type = spec.type ?? 'lowpass';
    f.Q.value = spec.q ?? (f.type === 'bandpass' ? 1.5 : 0.7);
    if (spec.gain !== undefined) f.gain.value = spec.gain;
    const t0 = this.t + (spec.at ?? 0);
    f.frequency.setValueAtTime(this.hz(spec.f), this.t);
    if (spec.f1 !== undefined) {
      const t1 = t0 + (spec.dur ?? 0.1);
      f.frequency.setValueAtTime(this.hz(spec.f), t0);
      f.frequency.exponentialRampToValueAtTime(this.hz(spec.f1), t1);
      if (spec.f2 !== undefined) f.frequency.exponentialRampToValueAtTime(this.hz(spec.f2), t1 + (spec.dur2 ?? 0.1));
    }
    if (to) f.connect(to);
    return f;
  }

  shaper(amount: number, to: AudioNode | null = this.out): WaveShaperNode {
    const s = this.track(this.ctx.createWaveShaper());
    s.curve = driveCurve(amount);
    s.oversample = '2x';
    if (to) s.connect(to);
    return s;
  }

  /** filter → filter2 → drive → gain → `to`; returns the chain input. */
  chain(spec: ChainSpec): AudioNode {
    let node: AudioNode = this.gain(spec.gain ?? 1, spec.to ?? this.out);
    if (spec.dist) node = this.shaper(spec.dist, node);
    if (spec.filter2) node = this.filter(spec.filter2, node);
    if (spec.filter) node = this.filter(spec.filter, node);
    return node;
  }

  /** Feedback echo (delay → lowpass → feedback); returns the input. Dry signal is not passed. */
  echo(time: number, feedback: number, wet: number, lp = 3500, to: AudioNode = this.out): AudioNode {
    const input = this.gain(1, null);
    const delay = this.track(this.ctx.createDelay(2));
    delay.delayTime.value = time;
    const fb = this.gain(feedback, delay);
    const tone = this.filter({ type: 'lowpass', f: lp / this.p }, fb);
    const out = this.gain(wet, to);
    input.connect(delay);
    delay.connect(tone);
    delay.connect(out);
    return input;
  }

  /** Register a source; schedules start/stop and remembers the last one to end. */
  source<T extends AudioScheduledSourceNode>(n: T, at: number, stopAt: number | null): T {
    this.track(n);
    this.srcs.push(n);
    n.start(Math.max(this.t + at, 0));
    this.scheduleStop(n, stopAt);
    return n;
  }

  private scheduleStop(n: AudioScheduledSourceNode, stopAt: number | null): void {
    if (stopAt === null) {
      if (!this.openSrc) this.openSrc = n;
      return;
    }
    const s = this.t + stopAt;
    n.stop(s);
    if (s >= this.end) {
      this.end = s;
      this.lastSrc = n;
    }
  }

  /** Keep the voice alive (e.g. for an echo tail) until `at` seconds after start. */
  sustain(at: number): void {
    const c = this.ctx.createConstantSource();
    c.offset.value = 0;
    c.connect(this.out);
    this.source(c, 0, at);
  }

  /** Amplitude envelope on `param` starting at `at`; returns the end time (relative). */
  env(param: AudioParam, at: number, e: Env): number {
    const t0 = this.t + at;
    const a = Math.max(0.001, e.a ?? 0.002);
    const h = e.h ?? 0;
    const d = Math.max(0.005, e.d);
    const pk = e.peak ?? 1;
    param.setValueAtTime(0, t0);
    param.linearRampToValueAtTime(pk, t0 + a);
    if (h > 0) param.setValueAtTime(pk, t0 + a + h);
    if (e.lin) param.linearRampToValueAtTime(0, t0 + a + h + d);
    else {
      param.exponentialRampToValueAtTime(Math.min(SILENT, pk * 0.01) || SILENT, t0 + a + h + d);
      param.setValueAtTime(0, t0 + a + h + d + 0.002);
    }
    return at + a + h + d;
  }

  /** Schedules a pitch trajectory onto a frequency param (`mul` scales it, e.g. FM ratio). */
  pitch(param: AudioParam, at: number, s: PitchSpec, mul = 1): void {
    const t0 = this.t + at + (s.fat ?? 0);
    param.setValueAtTime(this.hz(s.f * mul), this.t + at);
    if (s.f1 !== undefined) {
      param.setValueAtTime(this.hz(s.f * mul), t0);
      const t1 = t0 + (s.fdur ?? 0.1);
      param.exponentialRampToValueAtTime(this.hz(s.f1 * mul), t1);
      if (s.f2 !== undefined) param.exponentialRampToValueAtTime(this.hz(s.f2 * mul), t1 + (s.f2dur ?? 0.1));
    }
  }

  /** Bare oscillator started at `at`, stopped at `stopAt` (null = runs until kill()). */
  osc(type: OscillatorType, f: number, at: number, stopAt: number | null, to: AudioNode | null): OscillatorNode {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = this.hz(f);
    if (to) o.connect(to);
    return this.source(o, at, stopAt);
  }

  /** Looping noise source with a random start offset. */
  noiseSrc(color: NoiseColor, at: number, stopAt: number | null, to: AudioNode | null, rate = 1): AudioBufferSourceNode {
    const n = this.ctx.createBufferSource();
    n.buffer = noiseBuffer(this.ctx, color);
    n.loop = true;
    n.playbackRate.value = Math.max(0.05, rate * this.p);
    if (to) n.connect(to);
    this.track(n);
    this.srcs.push(n);
    n.start(Math.max(this.t + at, 0), Math.random() * 1.5);
    this.scheduleStop(n, stopAt);
    return n;
  }

  /** LFO added onto `param` (depth in the param's units). */
  lfo(param: AudioParam, rate: number, depth: number, at: number, stopAt: number | null, type: OscillatorType = 'sine'): OscillatorNode {
    const g = this.gain(depth, null);
    g.connect(param);
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = rate;
    o.connect(g);
    return this.source(o, at, stopAt);
  }

  /** Enveloped oscillator with pitch trajectory, optional filter/drive. */
  tone(s: ToneSpec): { osc: OscillatorNode; amp: GainNode } {
    const at = s.at ?? 0;
    let dest: AudioNode = s.to ?? this.out;
    if (s.dist) dest = this.shaper(s.dist, dest);
    if (s.filter) dest = this.filter({ ...s.filter, at: (s.filter.at ?? 0) + at }, dest);
    const amp = this.gain(0, dest);
    const end = this.env(amp.gain, at, s);
    const o = this.osc(s.type ?? 'sine', s.f, at, end + 0.02, amp);
    if (s.detune) o.detune.value = s.detune;
    this.pitch(o.frequency, at, s);
    if (s.vib) this.lfo(o.detune, s.vib[0], s.vib[1], at, end + 0.02);
    return { osc: o, amp };
  }

  /** Two-operator FM tone (sine modulator by default). */
  fm(s: FmSpec): { osc: OscillatorNode; amp: GainNode } {
    const at = s.at ?? 0;
    const { osc, amp } = this.tone(s);
    const len = (s.a ?? 0.002) + (s.h ?? 0) + s.d;
    const dev = this.gain(0, null);
    dev.connect(osc.frequency);
    const fMod = s.f * s.ratio;
    const t0 = this.t + at;
    dev.gain.setValueAtTime(this.hz(fMod) * s.index, t0);
    dev.gain.linearRampToValueAtTime(this.hz(fMod) * (s.index1 ?? 0), t0 + (s.idur ?? len));
    const m = this.osc(s.modType ?? 'sine', fMod, at, at + len + 0.02, dev);
    this.pitch(m.frequency, at, s, s.ratio);
    return { osc, amp };
  }

  /** Enveloped, filtered noise burst. */
  noise(s: NoiseSpec): { src: AudioBufferSourceNode; amp: GainNode } {
    const at = s.at ?? 0;
    let dest: AudioNode = s.to ?? this.out;
    if (s.dist) dest = this.shaper(s.dist, dest);
    if (s.filter2) dest = this.filter({ ...s.filter2, at: (s.filter2.at ?? 0) + at }, dest);
    if (s.filter) dest = this.filter({ ...s.filter, at: (s.filter.at ?? 0) + at }, dest);
    const amp = this.gain(0, dest);
    const end = this.env(amp.gain, at, s);
    const src = this.noiseSrc(s.color ?? 'white', at, end + 0.02, amp, s.rate ?? 1);
    return { src, amp };
  }

  /** Gated noise grains: electric crackle, debris, rattles. */
  crackle(s: CrackleSpec): void {
    const at = s.at ?? 0;
    let dest: AudioNode = s.to ?? this.out;
    if (s.filter) dest = this.filter({ ...s.filter, at: (s.filter.at ?? 0) + at }, dest);
    const g = this.gain(0, dest);
    const grain = s.grain ?? 0.004;
    const t0 = this.t + at;
    g.gain.setValueAtTime(0, t0);
    let x = Math.random() / s.rate;
    while (x < s.dur) {
      const k = s.fade ? Math.pow(1 - x / s.dur, 1.5) : 1;
      const amp = s.peak * k * (0.35 + Math.random() * 0.65);
      const len = grain * (0.5 + Math.random());
      g.gain.setValueAtTime(amp, t0 + x);
      g.gain.setValueAtTime(0, t0 + x + len);
      x += len + (-Math.log(1 - Math.random() * 0.999) / s.rate);
    }
    this.noiseSrc(s.color ?? 'white', at, at + s.dur + 0.02, g);
  }

  /** Stop every source at `when` (absolute time) and clean up once they end. */
  kill(when: number): void {
    if (this.done) return;
    for (const s of this.srcs) {
      try {
        s.stop(when);
      } catch {
        // already stopped
      }
    }
    // Every source now ends by `when`; a still-open (loop) source ends exactly then.
    if (this.openSrc) this.lastSrc = this.openSrc;
    this.end = Math.min(this.end || when, when);
    this.finish();
  }

  /** Arm the cleanup: disconnect the whole graph when the last source ends. */
  finish(): void {
    if (this.done) return;
    const last = this.lastSrc;
    if (!last) {
      if (this.srcs.length === 0) this.dispose();
      return;
    }
    last.onended = () => this.dispose();
  }

  /** Disconnect every node immediately (idempotent). */
  dispose(): void {
    if (this.done) return;
    this.done = true;
    for (const s of this.srcs) s.onended = null;
    for (const n of this.nodes) {
      try {
        n.disconnect();
      } catch {
        // ignore
      }
    }
    liveNodes -= this.nodes.length;
    this.nodes.length = 0;
    this.srcs.length = 0;
    this.lastSrc = null;
    const cb = this.onDone;
    this.onDone = null;
    if (cb) cb();
  }

  get disposed(): boolean {
    return this.done;
  }
}
